// Достижения персонажа: открытые — с датой (свежие сверху, ещё не виденные — с пометкой «новое»),
// закрытые — серые с прогрессом к цели. Открыв карточку, человек их увидел — маскот больше не объявляет.

import { useEffect, useState } from 'react'

import { Badge, Card } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDayMonth, formatNumber } from '@/lib/format'

import { useAchievements, type Achievement } from './api'
import { useSeenAchievements } from './mascot/logic'

function order(list: Achievement[]): Achievement[] {
  const open = list.filter((a) => a.unlocked_at).sort((a, b) => b.unlocked_at!.localeCompare(a.unlocked_at!))
  const closed = list.filter((a) => !a.unlocked_at).sort((a, b) => b.progress / b.target - a.progress / a.target)
  return [...open, ...closed]
}

function Tile({ item, fresh }: { item: Achievement; fresh: boolean }) {
  const open = Boolean(item.unlocked_at)
  const share = Math.min(1, item.progress / item.target)
  return (
    <li className={cn('flex gap-3 rounded-xl border p-3', open ? 'border-accent/40 bg-accent-soft' : 'border-border')}>
      <span
        aria-hidden
        className={cn(
          'grid size-11 shrink-0 place-items-center rounded-full text-2xl',
          open ? 'bg-surface shadow-sm' : 'bg-surface-2 opacity-60 grayscale',
        )}
      >
        {item.icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 text-sm font-semibold">
          <span className={open ? 'text-fg' : 'text-muted'}>{item.title}</span>
          {fresh ? <Badge tone="accent">новое</Badge> : null}
        </p>
        <p className="text-xs text-muted">{item.description}</p>
        {open ? (
          <p className="mt-1 text-xs text-accent">Открыто {formatDayMonth(item.unlocked_at!)}</p>
        ) : (
          <div className="mt-1.5">
            <div
              className="h-1.5 overflow-hidden rounded-full bg-surface-2"
              role="progressbar"
              aria-label={`Прогресс: ${item.title}`}
              aria-valuemin={0}
              aria-valuemax={item.target}
              aria-valuenow={item.progress}
            >
              <div className="h-full rounded-full bg-accent/60" style={{ width: `${share * 100}%` }} />
            </div>
            <p className="mt-0.5 text-xs text-muted">
              {formatNumber(item.progress)} / {formatNumber(item.target)} {item.unit}
            </p>
          </div>
        )}
      </div>
    </li>
  )
}

export function AchievementsCard({ className }: { className?: string }) {
  const query = useAchievements()
  const [seen, markSeen] = useSeenAchievements()
  // Что было новым при открытии карточки — пометки держатся, пока карточка на экране.
  const [freshAtOpen, setFreshAtOpen] = useState<Set<string> | null>(null)
  const list = Array.isArray(query.data) ? query.data : null
  const unlocked = list?.filter((a) => a.unlocked_at).map((a) => a.key) ?? []
  const unseen = unlocked.filter((k) => !seen.has(k))
  if (list && freshAtOpen === null) setFreshAtOpen(new Set(unseen))

  const unseenKey = unseen.join(',')
  useEffect(() => {
    if (unseenKey) markSeen(unseenKey.split(','))
  }, [unseenKey, markSeen])

  if (!list) return null
  return (
    <Card title="Достижения" subtitle={`Открыто ${unlocked.length} из ${list.length}`} className={className}>
      <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {order(list).map((item) => (
          <Tile key={item.key} item={item} fresh={freshAtOpen?.has(item.key) ?? false} />
        ))}
      </ul>
    </Card>
  )
}
